import { ActiveReportStore } from "../stores";
import { useContext, useEffect, useRef } from "react";
import { PbiContext } from "../components/PbiContext";

export const useEmbeddedPowerBiWebWorker = () => {
  const [ActiveReportParams, setActiveEmbedConfig] = ActiveReportStore((s) => [
    s.activeReportParams,
    s.setActiveEmbedConfig,
  ]);
  const { webWorkerProps } = useContext(PbiContext);

  const workerRef = useRef<Worker | null>(null);

  // If caller hasn't provided the hook props needed for the worker, bail out
  useEffect(() => {
    if (!webWorkerProps?.webWorkerGetNewAccessToken) return;

    let mounted = true;
    let worker: Worker | null = null;

    try {
      if (typeof Worker !== "undefined") {
        worker = new Worker(
          new URL("../workers/checkPowerBiAccessToken.ts", import.meta.url),
          { type: "module" }
        );
        workerRef.current = worker;
      }
    } catch {
      // If worker creation fails in the environment (e.g. Node test runner),
      // stop trying to use the worker.
      return;
    }

    if (!worker) return;

    const onMessage = async (event: MessageEvent) => {
      if (!mounted) return;
      const { result } = event.data || {};

      if (result && ActiveReportParams?.reportId) {
        const newToken = await webWorkerProps?.webWorkerGetNewAccessToken(
          ActiveReportParams.reportId,
          ActiveReportParams.filter
        );
        if (newToken) {
          try {
            worker.postMessage({ currentTokenExpDate: newToken.expiration });
          } catch {
            // best-effort post
            try {
              worker.postMessage({ currentTokenExpDate: newToken.expiration });
            } catch {
              /* ignore */
            }
          }

          // Update state with the new token
          // @ts-ignore - setActiveEmbedConfig typing mismatch for brevity
          setActiveEmbedConfig((prevState) => ({ ...prevState, accessToken: newToken.token }));
        }
      }
    };

    worker.addEventListener("message", onMessage);

    // Publish a test-friendly termination helper; preserve any existing helper
    // and restore it on cleanup.
    // @ts-ignore
    const previousHelper = globalThis.__terminateEmbeddedPowerBiWorker;
    // @ts-ignore
    globalThis.__terminateEmbeddedPowerBiWorker = () => {
      try {
        worker?.terminate();
      } catch {
        /* ignore */
      }
    };

    return () => {
      mounted = false;
      try {
        worker?.removeEventListener("message", onMessage);
      } catch {
        /* ignore */
      }
      try {
        worker?.terminate();
      } catch {
        /* ignore */
      }

      // restore previous global helper if present, otherwise delete
      // @ts-ignore
      if (previousHelper) {
        // @ts-ignore
        globalThis.__terminateEmbeddedPowerBiWorker = previousHelper;
      } else {
        // @ts-ignore
        delete globalThis.__terminateEmbeddedPowerBiWorker;
      }
    };
    // NOTE: ActiveReportParams and setActiveEmbedConfig may change; include
    // them so we can react to changes in the active report configuration.
  }, [webWorkerProps?.webWorkerGetNewAccessToken, ActiveReportParams, setActiveEmbedConfig]);

  return null;
};
